import {
  type CardDraftBulkImport,
  type BulkImportStore,
} from '@/components/BulkImportModal/store/bulk-import.store.types'
import {
  BASE_RESPOND_FIELD_VALUES,
  type CardActivityType,
  type CardActivityWithId,
} from '@/models/card-activity/card-activity.model'
import generateGradingCriteria from '@/utils/generateGradingCriteria'
import { checkIsWrittenRespondCard } from '@/views/NewBuilder/utils/check-type-answer-respond-card'

type GradingCriteriaResult =
  | {
      criteria: string
      title: string
    }
  | {
      error: true
      message: string
    }

interface GradingCriteriaSuccess {
  criteria: string
  title: string
}

interface GradingCriteriaError {
  error: true
  message: string
}

function isGradingCriteriaSuccess(
  result: GradingCriteriaSuccess | GradingCriteriaError,
): result is GradingCriteriaSuccess {
  return !('error' in result) && 'criteria' in result && 'title' in result
}

export async function enrichRespondCard(
  cardType: CardActivityType,
  cardDraft: CardDraftBulkImport & { id: string },
  settings: BulkImportStore['settings'],
) {
  const isWritten = checkIsWrittenRespondCard(cardType)

  const gradingMethod = settings.grading.method

  const baseRespondCard: Partial<CardActivityWithId> = {
    allowRetries: BASE_RESPOND_FIELD_VALUES.allowRetries,
    id: cardDraft.id,
    type: cardType,
    prompt: cardDraft.prompt,
    language: settings.language,
    default_language: settings.defaultLanguage,
    grading_method: gradingMethod,
    rubricId: settings.grading.rubricId,
    grading_criteria: settings.grading.performanceExpectations,
    grading_criteria_title: '',
    standardId: settings.grading.standardId,
    target_proficiency_level: settings.grading.target_proficiency_level,
    scoring_type: 'pass_fail',
    autoGrade: true,
    feedback_language: settings.feedbackLanguage,
    feedback_types: settings.feedback,
    points: settings.points,
  }

  if (gradingMethod === 'standards_based') {
    delete baseRespondCard.rubricId
    delete baseRespondCard.grading_criteria
    delete baseRespondCard.grading_criteria_title
  } else if (gradingMethod === 'rubric') {
    delete baseRespondCard.grading_criteria
    delete baseRespondCard.grading_criteria_title
    delete baseRespondCard.standardId
    delete baseRespondCard.target_proficiency_level
  } else if (gradingMethod === 'simple') {
    delete baseRespondCard.rubricId
    delete baseRespondCard.standardId
    delete baseRespondCard.target_proficiency_level
  } else if (gradingMethod === 'manual') {
    delete baseRespondCard.rubricId
    delete baseRespondCard.grading_criteria
    delete baseRespondCard.grading_criteria_title
    delete baseRespondCard.standardId
    delete baseRespondCard.target_proficiency_level
  }

  if (isWritten) {
    baseRespondCard.maxCharacters = BASE_RESPOND_FIELD_VALUES.maxCharacters
  } else {
    baseRespondCard.respondTime = BASE_RESPOND_FIELD_VALUES.respondTime
  }

  if (settings.grading.gradingCriteriaWithAI && settings.grading.method === 'simple') {
    const gradingCriteria = (await generateGradingCriteria({
      prompt: cardDraft.prompt,
      level: settings.grading.studentLevel,
      focus: settings.grading.aiFocus,
      instructions: settings.grading.performanceExpectations,
    })) as GradingCriteriaResult

    if (gradingCriteria && isGradingCriteriaSuccess(gradingCriteria)) {
      baseRespondCard.grading_criteria = gradingCriteria.criteria
      baseRespondCard.grading_criteria_title = gradingCriteria.title
    }
  }

  return baseRespondCard
}
